"use client";

import {
  type MouseEvent,
  type Ref,
  useEffect,
  useMemo,
  type RefObject,
  type FC,
} from "react";
import { useTranslation } from "react-i18next";
import { isWindowDefined, mergeClasses } from "@huub-web/utils";

import { Flex } from "../Flex";
import { Select, type SelectListOption } from "../Select";
import type { ChildrenProp } from "../../props";
import type { ContainerProps } from "../Container";
import { useMaybeContentMenu } from "../../hooks/useContentMenuSection";
import { useDelayedState } from "../../hooks/useDelayedState";
import type { ContentMenuSection } from "../../contexts/ContentMenuContext";

import { sortSectionsByDOMPosition } from "./utils/sortSectionsByDOMPosition";
import { ContentMenuItem } from "./components/ContentMenuItem";
import type { CarouselRef } from "../Carousel";

import {
  containerStyle,
  menuStyle,
  carouselMenuStyle,
  mobileSelectStyle,
  desktopMenuStyle,
} from "./ContentMenu.css";

const MAX_TOTAL_BETWEEN_CHEVRONS = 6;
const HEADER_HEIGHT = 64;

export type ContentMenuProps = ChildrenProp & {
  /**
   * Size of the container
   */
  container?: ContainerProps["size"];
  /**
   * Scroll offset of the content menu
   * - 128 + 16: default scroll offset
   */
  scrollOffset?: number;
  /**
   * Variant of the content menu
   * - "marketing": marketing variant
   * - "app": app variant
   * - "carousel": carousel variant
   */
  variant?: "marketing" | "app" | "carousel";
  /**
   * Mode of operation
   * - "page" (default): scrolls the page to sections
   * - "carousel": navigates carousel via carouselRef
   */
  mode?: "page" | "carousel";
  /**
   * Required when mode="carousel"
   * Reference to the carousel for navigation
   */
  carouselRef?: RefObject<CarouselRef | null>;
  /**
   * Required when mode="carousel"
   * Current active index from carousel's onIndexChange
   */
  activeIndex?: number;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLElement>;
};

/**
 * ContentMenu component displays navigation menu items
 * for all registered sections and handles smooth scrolling
 */
export const ContentMenu: FC<ContentMenuProps> = ({
  variant = "marketing",
  scrollOffset = 128 + 16,
  mode = "page",
  carouselRef,
  activeIndex: activeIndexProp,
  children,
  ref,
}) => {
  const { t } = useTranslation();
  const contentMenu = useMaybeContentMenu();
  const registeredSections = contentMenu?.sections ?? [];

  const isCarouselMode = mode === "carousel";
  const effectiveVariant = isCarouselMode ? "carousel" : variant;

  const [activeID, setDebouncedActiveID, setImmediateActiveID] =
    useDelayedState<string | null>(null, isCarouselMode ? 600 : 0);

  /**
   * Sort sections by their DOM position to handle async additions
   * Recalculate when sections change
   */
  const sections = useMemo(
    () =>
      sortSectionsByDOMPosition(
        registeredSections,
        isCarouselMode ? "horizontal" : "vertical",
      ).filter((s) => !s.disable),
    [registeredSections, isCarouselMode],
  );

  /**
   * In carousel mode, derive activeID from activeIndexProp
   */
  useEffect(() => {
    if (
      isCarouselMode &&
      activeIndexProp !== undefined &&
      sections.length > 0
    ) {
      const section = sections[activeIndexProp];
      if (section) {
        setDebouncedActiveID(section.id);
      }
    }
  }, [isCarouselMode, activeIndexProp, sections]);

  /**
   * Calculate which section is currently active based on scroll position
   * A section is active when it's closest to the sticky menu threshold
   * Only used in page mode
   */
  useEffect(() => {
    if (isCarouselMode || !isWindowDefined() || sections.length === 0) {
      return;
    }

    const handleScroll = () => {
      // Calculate the sticky menu threshold (bottom edge of the sticky menu)
      // Using a fixed threshold based on the sticky top position plus menu height
      const threshold = HEADER_HEIGHT + 16;

      let closestSection: string | null = null;
      let closestDistance = Infinity;

      sections.forEach((section) => {
        if (section.ref.current) {
          const rect = section.ref.current.getBoundingClientRect();
          const distance = Math.abs(rect.top - threshold);

          // Prefer sections that are at or just below the threshold
          // If section top is above threshold, it's currently visible
          if (rect.top <= threshold && rect.bottom > threshold) {
            // Section is crossing the threshold - prioritize it
            if (distance < closestDistance) {
              closestDistance = distance;
              closestSection = section.id;
            }
          } else if (rect.top > threshold) {
            // Section is below threshold - consider it if no better match
            if (distance < closestDistance) {
              closestDistance = distance;
              closestSection = section.id;
            }
          }
        }
      });

      if (closestSection && closestSection !== activeID) {
        setDebouncedActiveID(closestSection);
      }
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sections, activeID, isCarouselMode]);

  /**
   * Navigate to a section - either scroll page or carousel
   */
  const navigateToSection = (section: ContentMenuSection) => {
    if (isCarouselMode && carouselRef?.current) {
      const sectionIndex = sections.findIndex((s) => s.id === section.id);
      if (sectionIndex !== -1) {
        carouselRef.current.setIndex(sectionIndex);
      }
    } else if (section?.ref.current && isWindowDefined()) {
      const elementPosition = section.ref.current.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - scrollOffset;

      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  /**
   * Navigate to index - for carousel mode
   */
  const navigateToIndex = (index: number) => {
    const section = sections[index];
    if (section) {
      setImmediateActiveID(section.id);
    }

    if (isCarouselMode && carouselRef?.current) {
      carouselRef.current.setIndex(index);
    } else if (section) {
      navigateToSection(section);
    }
  };

  const activeIndex = sections.findIndex((s) => s.id === activeID);

  const handleClick = (e: MouseEvent, id: string) => {
    e.preventDefault();
    const section = sections.find((s) => s.id === id);
    if (section) {
      setImmediateActiveID(id);
      navigateToSection(section);
    }
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      navigateToIndex(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < sections.length - 1) {
      navigateToIndex(activeIndex + 1);
    }
  };

  /**
   * Handle clicking left ellipsis - jump to section just before the first visible item
   */
  const handleLeftEllipsis = () => {
    const { startIndex } = middleSpan;
    if (startIndex > 0) {
      const targetSection = middleSections[startIndex - 1];
      const sectionIndex = sections.findIndex((s) => s.id === targetSection.id);
      if (sectionIndex !== -1) {
        navigateToIndex(sectionIndex);
      }
    }
  };

  /**
   * Handle clicking right ellipsis - jump to section just after the last visible item
   */
  const handleRightEllipsis = () => {
    const { endIndex } = middleSpan;
    if (endIndex < middleSections.length - 1) {
      const targetSection = middleSections[endIndex + 1];
      const sectionIndex = sections.findIndex((s) => s.id === targetSection.id);
      if (sectionIndex !== -1) {
        navigateToIndex(sectionIndex);
      }
    }
  };

  const leadingSections = sections.filter((s) => s.forcePosition === "leading");

  const trailingSections = sections.filter(
    (s) => s.forcePosition === "trailing",
  );

  const leadingInsideSections = sections.filter(
    (s) => s.forcePosition === "leadingInside",
  );

  const middleSections = [
    ...leadingInsideSections,
    ...sections.filter(
      (s) =>
        s.forcePosition !== "leading" &&
        s.forcePosition !== "leadingInside" &&
        s.forcePosition !== "trailing",
    ),
  ];

  /**
   * Find the active index within middleSections
   */
  const activeMiddleIndex = middleSections.findIndex((s) => s.id === activeID);

  /**
   * Select options for mobile view
   */
  const selectOptions: SelectListOption[] = sections.map((section) => ({
    value: section.id,
    label: section.label,
  }));

  /**
   * Handle select change for mobile view
   * Uses setTimeout to allow the Select popup to close before scrolling
   */
  const handleSelectChange = (
    value: string | number | boolean | (string | number)[],
  ) => {
    const sectionId = String(value);
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      // Delay navigation to allow Select popup to close first
      setTimeout(() => {
        navigateToSection(section);
      }, 0);
    }
  };

  /**
   * Calculate visible span of middle sections.
   * Total items + ellipses between chevrons never exceeds MAX_TOTAL_BETWEEN_CHEVRONS.
   * Only re-centers when the active item falls outside the current window.
   */
  const middleSpan = (() => {
    const totalItems = middleSections.length;
    const maxSlots = Math.max(
      1,
      MAX_TOTAL_BETWEEN_CHEVRONS - leadingSections.length,
    );

    if (totalItems <= maxSlots) {
      return {
        visibleSections: middleSections,
        showLeftEllipsis: false,
        showRightEllipsis: false,
        startIndex: 0,
        endIndex: totalItems - 1,
      };
    }

    let startIndex: number;
    let endIndex: number;

    if (activeMiddleIndex < 0) {
      startIndex = 0;
      endIndex = maxSlots - 1;
    } else {
      const halfSpan = Math.floor(maxSlots / 2);
      startIndex = activeMiddleIndex - halfSpan;
      if (startIndex < 0) startIndex = 0;
      endIndex = startIndex + maxSlots - 1;
      if (endIndex >= totalItems) {
        endIndex = totalItems - 1;
        startIndex = Math.max(0, endIndex - maxSlots + 1);
      }
    }

    const showLeftEllipsis = startIndex > 0;
    const showRightEllipsis = endIndex < totalItems - 1;

    const ellipsisCount =
      (showLeftEllipsis ? 1 : 0) + (showRightEllipsis ? 1 : 0);
    const itemBudget = maxSlots - ellipsisCount;

    if (showLeftEllipsis && !showRightEllipsis) {
      startIndex = endIndex - itemBudget + 1;
    } else if (!showLeftEllipsis && showRightEllipsis) {
      endIndex = startIndex + itemBudget - 1;
    } else if (showLeftEllipsis && showRightEllipsis) {
      const halfItem = Math.floor(itemBudget / 2);
      startIndex = activeMiddleIndex - halfItem;
      if (startIndex < 1) startIndex = 1;
      endIndex = startIndex + itemBudget - 1;
      if (endIndex >= totalItems - 1) {
        endIndex = totalItems - 2;
        startIndex = Math.max(1, endIndex - itemBudget + 1);
      }
    }

    // If only 1 item is hidden on a side, absorb it instead of showing ...
    if (startIndex === 1) {
      startIndex = 0;
    }
    if (endIndex === totalItems - 2) {
      endIndex = totalItems - 1;
    }

    const visibleSections = middleSections.slice(startIndex, endIndex + 1);

    return {
      visibleSections,
      showLeftEllipsis: startIndex > 0,
      showRightEllipsis: endIndex < totalItems - 1,
      startIndex,
      endIndex,
    };
  })();

  return (
    <nav ref={ref} className={containerStyle({ variant: effectiveVariant })}>
      {/* Mobile: Select dropdown */}
      <div className={mobileSelectStyle}>
        <Select
          value={activeID ?? undefined}
          options={selectOptions}
          onChange={handleSelectChange}
          placeholder={t("contentMenu.selectSection")}
          variant="opaque"
        />
      </div>

      {/* Desktop: Menu items */}
      <Flex
        as="ul"
        className={mergeClasses(
          menuStyle,
          desktopMenuStyle,
          isCarouselMode ? carouselMenuStyle : undefined,
        )}
        align="center"
        justify="center"
        wrap
      >
        {leadingSections?.map((section) => (
          <ContentMenuItem
            key={section.id}
            active={section.id === activeID}
            onClick={(e) => handleClick(e, section.id)}
            icon={section.icon}
            tooltip={section.label}
            tooltipPlacement="bottom"
          />
        ))}

        <ContentMenuItem
          icon="chevronLeft"
          tooltip={t("contentMenu.previous")}
          variant="navigation"
          disabled={activeIndex <= 0 || sections.length === 0}
          onClick={handlePrevious}
          tooltipPlacement="bottom"
        />

        {middleSpan.showLeftEllipsis && (
          <ContentMenuItem
            variant="ellipsis"
            onClick={handleLeftEllipsis}
            tooltip={t("contentMenu.viewMore")}
            tooltipPlacement="bottom"
          />
        )}

        {middleSpan.visibleSections.map((section) => {
          const isLeadingInside = section.forcePosition === "leadingInside";
          const numberedIndex = isLeadingInside
            ? undefined
            : (section.index ??
              middleSections
                .filter((s) => s.forcePosition !== "leadingInside")
                .indexOf(section) + 1);
          return (
            <ContentMenuItem
              key={section.id}
              index={numberedIndex || undefined}
              label={section.label}
              icon={section.icon}
              active={section.id === activeID}
              onClick={(e) => handleClick(e, section.id)}
              tooltip={section.label}
              tooltipPlacement="bottom"
            />
          );
        })}

        {middleSpan.showRightEllipsis && (
          <ContentMenuItem
            variant="ellipsis"
            onClick={handleRightEllipsis}
            tooltip={t("contentMenu.viewMore")}
            tooltipPlacement="bottom"
          />
        )}

        <ContentMenuItem
          icon="chevronRight"
          tooltip={t("contentMenu.next")}
          disabled={activeIndex >= sections.length - 1 || sections.length === 0}
          onClick={handleNext}
          variant="navigation"
          tooltipPlacement="bottom"
        />

        {trailingSections?.map((section) => (
          <ContentMenuItem
            key={section.id}
            index={section.index}
            label={section.label}
            icon={section.icon}
            active={section.id === activeID}
            onClick={(e) => handleClick(e, section.id)}
            tooltipPlacement="bottom"
          />
        ))}
      </Flex>
      {children}
    </nav>
  );
};
